/**
 * Trust Overview — audit-first home dashboard.
 *
 * Answers: "Is my AI platform safe and being used properly?"
 *
 * Shows live KPIs from the activity trail (events/24h, blocked, warnings),
 * top users and agents by activity, recent violations, AI safety signals,
 * and active data flows. Every tile drills into the relevant sub-page.
 */

import { createFileRoute, Link } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import {
  ShieldCheck, ShieldAlert, ScrollText, Network, Bot, User,
  Lock, AlertTriangle, CheckCircle2, TrendingDown, Zap,
  ArrowRight, Activity, FileText, Flame, Eye, FolderArchive, Sparkles,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import {
  SEVERITY_STYLES,
  type ViolationPattern,
} from '@/lib/mock-trust-activity';
import {
  FRAMEWORK_STATUS_STYLES, FRAMEWORK_CATEGORY_STYLES,
} from '@/lib/mock-trust-data';
import {
  useTrustActivity,
  useTrustViolations,
  useTrustDataFlows,
} from '@/lib/use-trust-module';
import { useTrustAiSystems } from '@/lib/use-trust-ai-systems';
import { useTrustEntities, shortId } from '@/lib/use-trust-entities';
import { useTrustFrameworks } from '@/lib/use-trust-frameworks';
import { EvidenceExportPackDialog } from '@/components/trust/evidence-export-pack-dialog';

export const Route = createFileRoute('/trust/')({
  component: TrustOverviewPage,
});

const PATTERN_STATUS_STYLES: Record<ViolationPattern['status'], string> = {
  open: 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20',
  investigating: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20',
  resolved: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20',
  false_positive: 'bg-zinc-500/10 text-zinc-600 dark:text-zinc-400 border-zinc-500/20',
};

function TrustOverviewPage() {
  const [exportOpen, setExportOpen] = useState(false);

  // Real-data sources for the overview KPIs
  const { data: realEvents = [] } = useTrustActivity(1000);
  const { data: realViolations = [] } = useTrustViolations(200);
  const { data: realDataFlows = [] } = useTrustDataFlows();
  const { data: realAiSystems = [] } = useTrustAiSystems();
  const { data: realFrameworks = [] } = useTrustFrameworks();
  const entities = useTrustEntities();
  const ACTIVITY_EVENTS = realEvents;
  const VIOLATION_PATTERNS = realViolations;
  const DATA_FLOWS = realDataFlows;
  const AI_SYSTEMS = realAiSystems;
  const FRAMEWORKS = realFrameworks;
  void ACTIVITY_EVENTS; void VIOLATION_PATTERNS; void DATA_FLOWS; void AI_SYSTEMS; void FRAMEWORKS;

  // ── Event-derived KPIs ───────────────────────────────────────────────────
  const kpis = useMemo(() => {
    const now = Date.now();
    const cutoff24h = now - 86_400_000;
    const cutoff7d = now - 604_800_000;

    const events24h = ACTIVITY_EVENTS.filter(e => new Date(e.timestamp).getTime() >= cutoff24h);
    const events7d = ACTIVITY_EVENTS.filter(e => new Date(e.timestamp).getTime() >= cutoff7d);

    const blocked24h = events24h.filter(e => e.outcome.startsWith('blocked_')).length;
    const blocked7d = events7d.filter(e => e.outcome.startsWith('blocked_')).length;
    const warnings24h = events24h.filter(e => e.severity === 'warning' || e.severity === 'error' || e.severity === 'critical').length;
    const toolCalls24h = events24h.filter(e => e.category === 'tool_call' && e.outcome === 'success').length;
    const externalFlows24h = events24h.filter(e => e.externalTier && e.externalTier !== 'tier_1_iq').length;

    const uniqueUsers24h = new Set(events24h.map(e => e.userId).filter(Boolean)).size;
    const uniqueAgents24h = new Set(events24h.map(e => e.agentId).filter(Boolean)).size;

    return {
      events24h: events24h.length,
      events7d: events7d.length,
      blocked24h,
      blocked7d,
      warnings24h,
      toolCalls24h,
      externalFlows24h,
      uniqueUsers24h,
      uniqueAgents24h,
    };
  }, [ACTIVITY_EVENTS]);

  // ── Top users / agents by activity ──────────────────────────────────────
  const topUsers = useMemo(() => {
    const now = Date.now();
    const cutoff = now - 604_800_000; // 7d
    const m = new Map<string, { userId: string; count: number; blocked: number }>();
    ACTIVITY_EVENTS.forEach(e => {
      if (!e.userId || new Date(e.timestamp).getTime() < cutoff) return;
      const cur = m.get(e.userId) ?? { userId: e.userId, count: 0, blocked: 0 };
      cur.count++;
      if (e.outcome.startsWith('blocked_')) cur.blocked++;
      m.set(e.userId, cur);
    });
    return Array.from(m.values()).sort((a, b) => b.count - a.count).slice(0, 5);
  }, [ACTIVITY_EVENTS]);

  const topAgents = useMemo(() => {
    const now = Date.now();
    const cutoff = now - 604_800_000;
    const m = new Map<string, { agentId: string; count: number }>();
    ACTIVITY_EVENTS.forEach(e => {
      if (!e.agentId || new Date(e.timestamp).getTime() < cutoff) return;
      const cur = m.get(e.agentId) ?? { agentId: e.agentId, count: 0 };
      cur.count++;
      m.set(e.agentId, cur);
    });
    return Array.from(m.values()).sort((a, b) => b.count - a.count).slice(0, 5);
  }, [ACTIVITY_EVENTS]);

  // ── Recent violations ────────────────────────────────────────────────────
  const recentViolations = useMemo(
    () => VIOLATION_PATTERNS.filter(p => p.status === 'open' || p.status === 'investigating').slice(0, 3),
    [VIOLATION_PATTERNS],
  );

  // ── AI safety metrics ────────────────────────────────────────────────────
  const aiMetrics = useMemo(() => {
    const prod = AI_SYSTEMS.filter(a => a.status === 'in_production');
    const avgHall = prod.reduce((s, a) => s + (a.hallucinationRate30d ?? 0), 0) / Math.max(prod.length, 1);
    const totalInj = prod.reduce((s, a) => s + a.injectionAttempts30d, 0);
    const blocked = prod.reduce((s, a) => s + a.injectionsBlocked30d, 0);
    const blockRate = totalInj > 0 ? (blocked / totalInj) * 100 : 100;
    const avgFaith = prod.reduce((s, a) => s + (a.faithfulnessScore ?? 0), 0) / Math.max(prod.length, 1);
    return {
      count: prod.length,
      avgHall,
      blockRate,
      avgFaith,
      totalInj,
    };
  }, [AI_SYSTEMS]);

  // ── Framework posture ───────────────────────────────────────────────────
  const frameworkPosture = useMemo(() => {
    const enabled = FRAMEWORKS.filter(f => f.enabled);
    const totalControls = enabled.reduce((s, f) => s + f.controlCount, 0);
    const passingControls = enabled.reduce((s, f) => s + f.controlsPassing, 0);
    const score = totalControls > 0 ? Math.round((passingControls / totalControls) * 100) : 0;
    return { enabled, score, totalControls, passingControls };
  }, [FRAMEWORKS]);

  // ── High-anomaly data flows ─────────────────────────────────────────────
  const flaggedFlows = useMemo(
    () => DATA_FLOWS.filter(f => !f.approved || f.anomalyScore >= 50),
    [DATA_FLOWS],
  );

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="rounded-lg border bg-gradient-to-r from-blue-500/5 to-transparent border-blue-500/20 p-4 flex items-start gap-3">
        <ShieldCheck className="h-5 w-5 text-blue-600 dark:text-blue-400 shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm font-semibold mb-0.5">AI Platform Audit</p>
          <p className="text-xs text-muted-foreground">
            Every query, tool call, block, approval, and response on ArgusAI is an auditable event on the
            hash-chain. This dashboard summarizes activity, violations, and data flows over the last 24 hours.
          </p>
        </div>
        <Button size="sm" className="gap-1.5 shrink-0" onClick={() => setExportOpen(true)}>
          <FolderArchive className="h-3.5 w-3.5" /> Export Evidence
        </Button>
      </div>

      {/* Top KPI row — activity + blocks + warnings + tools + flows + framework */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <KpiCard
          label="Events (24h)"
          value={kpis.events24h.toLocaleString()}
          sublabel={`${kpis.events7d.toLocaleString()} this week`}
          icon={Activity}
          iconColor="text-primary"
          href="/trust/activity"
        />
        <KpiCard
          label="Blocked (24h)"
          value={kpis.blocked24h.toLocaleString()}
          sublabel="policy / injection / classification"
          icon={Lock}
          iconColor={kpis.blocked24h > 0 ? 'text-red-600 dark:text-red-400' : 'text-muted-foreground'}
          valueColor={kpis.blocked24h > 0 ? 'text-red-600 dark:text-red-400' : undefined}
          href="/trust/violations"
        />
        <KpiCard
          label="Warnings+ (24h)"
          value={kpis.warnings24h.toLocaleString()}
          sublabel="requires attention"
          icon={AlertTriangle}
          iconColor={kpis.warnings24h > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-muted-foreground'}
          href="/trust/activity"
        />
        <KpiCard
          label="Tool Calls (24h)"
          value={kpis.toolCalls24h.toLocaleString()}
          sublabel="successful MCP invocations"
          icon={Zap}
          iconColor="text-cyan-600 dark:text-cyan-400"
          href="/trust/activity"
        />
        <KpiCard
          label="External Flows (24h)"
          value={kpis.externalFlows24h.toLocaleString()}
          sublabel="data leaving platform"
          icon={Network}
          iconColor="text-pink-600 dark:text-pink-400"
          href="/trust/data-flows"
        />
        <KpiCard
          label="Compliance Score"
          value={`${frameworkPosture.score}%`}
          sublabel={`${frameworkPosture.passingControls}/${frameworkPosture.totalControls} controls`}
          icon={ShieldCheck}
          iconColor="text-emerald-600 dark:text-emerald-400"
          valueColor="text-emerald-600 dark:text-emerald-400"
          href="/trust/frameworks"
        />
      </div>

      {/* Two-column: Top users + Top agents */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <User className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                Top Users (7d)
              </h3>
              <Link to="/trust/activity" search={{ pivot: 'by_user' } as any}>
                <Button variant="ghost" size="sm" className="text-[10px] h-6 gap-1">
                  View all <ArrowRight className="h-3 w-3" />
                </Button>
              </Link>
            </div>
            <div className="space-y-2">
              {topUsers.length === 0 && (
                <p className="text-xs text-muted-foreground italic">No user activity in the last 7 days</p>
              )}
              {topUsers.map((u, idx) => {
                const user = entities.lookup.user(u.userId);
                const max = topUsers[0]!.count;
                return (
                  <div key={u.userId} className="flex items-center gap-2">
                    <span className="text-[10px] w-4 text-center text-muted-foreground">{idx + 1}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-xs font-medium truncate">{user?.name ?? `user:${shortId(u.userId)}`}</p>
                        <span className="text-[10px] text-muted-foreground">
                          {u.count} event{u.count === 1 ? '' : 's'}
                          {u.blocked > 0 && (
                            <span className="ml-1.5 text-red-600 dark:text-red-400">· {u.blocked} blocked</span>
                          )}
                        </span>
                      </div>
                      <Progress value={(u.count / max) * 100} className="h-1.5" />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <Bot className="h-4 w-4 text-pink-600 dark:text-pink-400" />
                Top Agents (7d)
              </h3>
              <Link to="/trust/activity" search={{ pivot: 'by_agent' } as any}>
                <Button variant="ghost" size="sm" className="text-[10px] h-6 gap-1">
                  View all <ArrowRight className="h-3 w-3" />
                </Button>
              </Link>
            </div>
            <div className="space-y-2">
              {topAgents.length === 0 && (
                <p className="text-xs text-muted-foreground italic">No agent activity in the last 7 days</p>
              )}
              {topAgents.map((a, idx) => {
                const agent = entities.lookup.agent(a.agentId);
                const max = topAgents[0]!.count;
                return (
                  <div key={a.agentId} className="flex items-center gap-2">
                    <span className="text-[10px] w-4 text-center text-muted-foreground">{idx + 1}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-xs font-medium truncate">{agent?.name ?? `agent:${shortId(a.agentId)}`}</p>
                        <span className="text-[10px] text-muted-foreground">
                          {a.count} event{a.count === 1 ? '' : 's'}
                        </span>
                      </div>
                      <Progress value={(a.count / max) * 100} className="h-1.5" />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent violations + Flagged flows */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Card className={recentViolations.length > 0 ? 'ring-1 ring-red-500/30' : ''}>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <ShieldAlert className={cn(
                  'h-4 w-4',
                  recentViolations.length > 0 ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400',
                )} />
                Open Violations
              </h3>
              <Link to="/trust/violations">
                <Button variant="ghost" size="sm" className="text-[10px] h-6 gap-1">
                  View all <ArrowRight className="h-3 w-3" />
                </Button>
              </Link>
            </div>
            {recentViolations.length === 0 ? (
              <p className="text-xs text-muted-foreground italic">No open violations — platform is running clean</p>
            ) : (
              <div className="space-y-2">
                {recentViolations.map(p => {
                  const sev = SEVERITY_STYLES[p.severity];
                  return (
                    <Link key={p.id} to="/trust/violations" className="block">
                      <div className="rounded-md border bg-muted/20 hover:bg-muted/40 transition-colors p-2.5">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <p className="text-xs font-medium leading-snug truncate">{p.title}</p>
                          <Badge variant="outline" className={cn('text-[9px] shrink-0', sev.color)}>{sev.label}</Badge>
                        </div>
                        <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                          <span className="capitalize">{p.patternType.replace(/_/g, ' ')}</span>
                          <span>·</span>
                          <Badge variant="outline" className={cn('text-[9px]', PATTERN_STATUS_STYLES[p.status])}>
                            {p.status.replace(/_/g, ' ')}
                          </Badge>
                          <span>·</span>
                          <span>{p.relatedEventIds.length} events</span>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className={flaggedFlows.length > 0 ? 'ring-1 ring-orange-500/30' : ''}>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <Network className={cn(
                  'h-4 w-4',
                  flaggedFlows.length > 0 ? 'text-orange-600 dark:text-orange-400' : 'text-emerald-600 dark:text-emerald-400',
                )} />
                Flagged Data Flows
              </h3>
              <Link to="/trust/data-flows">
                <Button variant="ghost" size="sm" className="text-[10px] h-6 gap-1">
                  View all <ArrowRight className="h-3 w-3" />
                </Button>
              </Link>
            </div>
            {flaggedFlows.length === 0 ? (
              <p className="text-xs text-muted-foreground italic">No unapproved or anomalous flows</p>
            ) : (
              <div className="space-y-2">
                {flaggedFlows.slice(0, 3).map(f => (
                  <Link key={f.id} to="/trust/data-flows" className="block">
                    <div className="rounded-md border bg-muted/20 hover:bg-muted/40 transition-colors p-2.5">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <p className="text-xs font-medium truncate">
                          {f.source.label} → {f.destination.label}
                        </p>
                        <Badge variant="outline" className={cn(
                          'text-[9px] shrink-0',
                          f.anomalyScore >= 70 ? 'bg-red-500/10 text-red-600 border-red-500/20' :
                            'bg-orange-500/10 text-orange-600 border-orange-500/20',
                        )}>
                          {f.anomalyScore}/100
                        </Badge>
                      </div>
                      <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
                        <code className="font-mono">{f.destination.host}</code>
                        {!f.approved && <Badge variant="outline" className="text-[9px] bg-red-500/10 text-red-600">Unapproved</Badge>}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* AI Safety Metrics */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-sm font-semibold">AI Safety Metrics</h2>
            <p className="text-xs text-muted-foreground">Real-time safety signals from ArgusAI's primitives</p>
          </div>
          <Link to="/trust/ai-systems">
            <Button variant="outline" size="sm" className="text-xs gap-1.5">
              AI Systems <ArrowRight className="h-3 w-3" />
            </Button>
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-muted-foreground">Production AI Systems</span>
                <Bot className="h-3.5 w-3.5 text-pink-600 dark:text-pink-400" />
              </div>
              <p className="text-2xl font-bold">{aiMetrics.count}</p>
              <p className="text-[10px] text-muted-foreground">monitored + inventoried</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-muted-foreground">Faithfulness Score</span>
                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
              </div>
              <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                {(aiMetrics.avgFaith * 100).toFixed(1)}%
              </p>
              <p className="text-[10px] text-muted-foreground">Threshold ≥ 92%</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-muted-foreground">Hallucination Rate (30d)</span>
                <TrendingDown className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
              </div>
              <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                {(aiMetrics.avgHall * 100).toFixed(2)}%
              </p>
              <p className="text-[10px] text-muted-foreground">Threshold &lt; 2%</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-muted-foreground">Injection Block Rate</span>
                <Zap className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
              </div>
              <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                {aiMetrics.blockRate.toFixed(1)}%
              </p>
              <p className="text-[10px] text-muted-foreground">{aiMetrics.totalInj} attempts (30d)</p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Framework coverage */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-sm font-semibold">Framework Evidence Coverage</h2>
            <p className="text-xs text-muted-foreground">How ArgusAI's internal controls map to your compliance frameworks</p>
          </div>
          <Link to="/trust/frameworks">
            <Button variant="outline" size="sm" className="text-xs gap-1.5">
              View All <ArrowRight className="h-3 w-3" />
            </Button>
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {frameworkPosture.enabled.map(fw => {
            const passingPct = fw.controlCount > 0 ? Math.round((fw.controlsPassing / fw.controlCount) * 100) : 0;
            const ss = FRAMEWORK_STATUS_STYLES[fw.status];
            const cs = FRAMEWORK_CATEGORY_STYLES[fw.category];
            return (
              <Link key={fw.id} to="/trust/frameworks/$frameworkId" params={{ frameworkId: fw.id }}>
                <Card className="hover:shadow-md transition-shadow cursor-pointer h-full">
                  <CardContent className="pt-4 pb-3">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="text-sm font-semibold">{fw.shortName}</p>
                        <p className="text-[10px] text-muted-foreground line-clamp-1">{fw.fullName}</p>
                      </div>
                      <Badge variant="outline" className={cn('text-[9px]', ss.color)}>{ss.label}</Badge>
                    </div>
                    <div className="space-y-1.5 mt-3">
                      <div className="flex items-center justify-between text-[10px]">
                        <span className="text-muted-foreground">Controls Passing</span>
                        <span className="font-semibold">{fw.controlsPassing}/{fw.controlCount}</span>
                      </div>
                      <Progress value={passingPct} className="h-1.5" />
                    </div>
                    <div className="flex items-center justify-between mt-3 pt-2 border-t">
                      <Badge variant="outline" className={cn('text-[8px] px-1', cs.color)}>{cs.label}</Badge>
                      <span className="text-[9px] text-muted-foreground">{fw.evidenceFresh} fresh evidence</span>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      </div>

      <EvidenceExportPackDialog open={exportOpen} onOpenChange={setExportOpen} />
    </div>
  );
}

// ─── KPI Card ───────────────────────────────────────────────────────────────

function KpiCard({
  label, value, sublabel, icon: Icon, iconColor, valueColor, href,
}: {
  label: string;
  value: string;
  sublabel: string;
  icon: typeof Activity;
  iconColor: string;
  valueColor?: string;
  href: string;
}) {
  return (
    <Link to={href as any}>
      <Card className="hover:shadow-md transition-shadow cursor-pointer h-full">
        <CardContent className="pt-4 pb-3">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-muted-foreground">{label}</span>
            <Icon className={cn('h-3.5 w-3.5', iconColor)} />
          </div>
          <p className={cn('text-2xl font-bold', valueColor)}>{value}</p>
          <p className="text-[10px] text-muted-foreground">{sublabel}</p>
        </CardContent>
      </Card>
    </Link>
  );
}
